import React, { useEffect, useRef, useState } from 'react'
import {
    AdGroupSelection,
    AdSelection,
    CampaignSelection,
    TargetSelection,
} from '../../components/selectors/CampaignSelector'
import { Button } from '../../components/ui/button'
import { Callout } from '../../components/ui/callout'
import type {
    AdGroupSelector,
    AdSelector,
    CampaignSelector,
    TargetSelector,
} from '../../types/filter'
import {
    customFieldApiError,
    customFieldsApi,
    type CustomFieldHistoryEntry,
    type CustomFieldValue,
} from './api'
import { CUSTOM_FIELD_DATA_TYPES } from './types'
import type { CustomFieldDefinition, CustomFieldEntityType } from './types'

const NUMBER_VALUE = /^-?(?:0|[1-9]\d{0,19})(?:\.\d{1,10})?$/
export const fieldClass = 'rounded border border-gray-300 px-3 py-2 text-sm'

export const HISTORY_SOURCE_LABELS: Record<CustomFieldHistoryEntry['source_type'], string> = {
    1: 'MerchJar UI',
    2: 'Public API',
    3: 'CSV import',
    4: 'History rollback',
    5: 'MerchJar system',
}

export type CustomFieldEntitySelection =
    | CampaignSelector
    | AdGroupSelector
    | TargetSelector
    | AdSelector

export const EMPTY_ENTITY_SELECTIONS: Record<CustomFieldEntityType, CustomFieldEntitySelection> = {
    campaign: { campaign_id: '', name: '', type: 'select-component' },
    ad_group: { ad_group_id: '', campaign_name: '', name: '', type: 'select-component' },
    target: { target_id: '', name: '', type: 'select-component' },
    ad: { ad_id: '', name: '', type: 'select-component' },
}

export const customFieldEntitySelectionId = (
    entityType: CustomFieldEntityType,
    selection: CustomFieldEntitySelection | null,
): string => {
    if (!selection) return ''
    if (entityType === 'campaign' && 'campaign_id' in selection) {
        return String(selection.campaign_id)
    }
    if (entityType === 'ad_group' && 'ad_group_id' in selection) {
        return String(selection.ad_group_id)
    }
    if (entityType === 'target' && 'target_id' in selection) {
        return String(selection.target_id)
    }
    if (entityType === 'ad' && 'ad_id' in selection) return String(selection.ad_id)
    return ''
}

export const EntitySelection = ({
    entityType,
    value,
    onChange,
    disabled,
}: {
    entityType: CustomFieldEntityType
    value: CustomFieldEntitySelection | null
    onChange: (value: CustomFieldEntitySelection) => void
    disabled: boolean
}) => {
    const selected = value ?? EMPTY_ENTITY_SELECTIONS[entityType]
    if (entityType === 'campaign') {
        return (
            <CampaignSelection
                value={selected as CampaignSelector}
                onChange={onChange}
                disabled={disabled}
            />
        )
    }
    if (entityType === 'ad_group') {
        return (
            <AdGroupSelection
                value={selected as AdGroupSelector}
                onChange={onChange}
                disabled={disabled}
            />
        )
    }
    if (entityType === 'target') {
        return (
            <TargetSelection
                value={selected as TargetSelector}
                onChange={onChange}
                disabled={disabled}
            />
        )
    }
    return <AdSelection value={selected as AdSelector} onChange={onChange} disabled={disabled} />
}

export const customFieldHistoryContext = (entry: CustomFieldHistoryEntry): string => {
    const actor = entry.actor_is_admin
        ? `MerchJar administrator${entry.changed_by_user_id ? ` ${entry.changed_by_user_id}` : ''} while impersonating`
        : entry.changed_by_user_id
          ? `User ${entry.changed_by_user_id}`
          : 'System'
    const reference = entry.source_reference ? ` · Reference: ${entry.source_reference}` : ''
    return `${HISTORY_SOURCE_LABELS[entry.source_type]} · ${actor}${reference}`
}

export const validateCustomFieldInput = (
    definition: Pick<CustomFieldDefinition, 'data_type'>,
    value: string | boolean | null,
): string | null => {
    if (value === null) return null
    if (definition.data_type === CUSTOM_FIELD_DATA_TYPES.Boolean) {
        return typeof value === 'boolean' ? null : 'Choose Yes, No, or Not set'
    }
    if (typeof value !== 'string' || value.length === 0) return 'Empty strings are not allowed'
    if (definition.data_type === CUSTOM_FIELD_DATA_TYPES.Number && !NUMBER_VALUE.test(value)) {
        return 'Enter a valid decimal number'
    }
    if (Array.from(value).length > 250) return 'Values must be at most 250 characters'
    return null
}

export const customFieldValueRequestIsCurrent = (
    request: number,
    latestRequest: number,
    requestedScope: { profileId: string; entityType: CustomFieldEntityType },
    activeScope: { profileId: string | null; entityType: CustomFieldEntityType },
): boolean =>
    request === latestRequest &&
    activeScope.profileId === requestedScope.profileId &&
    activeScope.entityType === requestedScope.entityType

export const ValueEditor = ({
    definition,
    entityId,
    profileId,
    entityType,
    current,
    valueQuotaReached,
    refresh,
}: {
    definition: CustomFieldDefinition
    entityId: string
    profileId: string
    entityType: CustomFieldEntityType
    current?: CustomFieldValue
    valueQuotaReached: boolean
    refresh: () => void
}) => {
    const initial = current?.value ?? null
    const [value, setValue] = useState<string | boolean | null>(initial)
    const [error, setError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const [history, setHistory] = useState<CustomFieldHistoryEntry[] | null>(null)
    const [historyCursor, setHistoryCursor] = useState<string | null>(null)
    const [historyLoading, setHistoryLoading] = useState(false)
    const latestHistoryRequest = useRef(0)
    const latestMutationRequest = useRef(0)
    const valueScope = `${profileId}:${entityType}:${entityId}:${definition.definition_id}`
    const activeValueScope = useRef(valueScope)
    activeValueScope.current = valueScope

    useEffect(() => {
        latestHistoryRequest.current++
        latestMutationRequest.current++
        setValue(current?.value ?? null)
        setError(null)
        setHistory(null)
        setHistoryCursor(null)
        setHistoryLoading(false)
        setSaving(false)
    }, [current, valueScope])

    const mutate = async (clear: boolean) => {
        const validation = clear ? null : validateCustomFieldInput(definition, value)
        if (validation) return setError(validation)
        const requestedScope = valueScope
        const request = ++latestMutationRequest.current
        setSaving(true)
        setError(null)
        try {
            await customFieldsApi.mutateValues(profileId, entityType, [
                {
                    field_definition_id: definition.definition_id,
                    entity_id: entityId,
                    operation: clear ? 'clear' : 'set',
                    ...(!clear ? { value: value as string | boolean } : {}),
                    expected_version: current?.version ?? null,
                },
            ])
            if (
                request === latestMutationRequest.current &&
                activeValueScope.current === requestedScope
            ) {
                refresh()
            }
        } catch (requestError) {
            if (
                request === latestMutationRequest.current &&
                activeValueScope.current === requestedScope
            ) {
                setError(customFieldApiError(requestError))
            }
        } finally {
            if (
                request === latestMutationRequest.current &&
                activeValueScope.current === requestedScope
            ) {
                setSaving(false)
            }
        }
    }

    const loadHistory = async (older = false) => {
        const requestedScope = valueScope
        if (activeValueScope.current !== requestedScope) return
        const request = ++latestHistoryRequest.current
        setHistoryLoading(true)
        setError(null)
        try {
            const page = await customFieldsApi.history(
                profileId,
                entityType,
                entityId,
                definition.definition_id,
                older ? (historyCursor ?? undefined) : undefined,
            )
            if (
                request === latestHistoryRequest.current &&
                activeValueScope.current === requestedScope
            ) {
                setHistory((currentHistory) =>
                    older ? [...(currentHistory ?? []), ...page.entries] : page.entries,
                )
                setHistoryCursor(page.nextCursor)
            }
        } catch (requestError) {
            if (
                request === latestHistoryRequest.current &&
                activeValueScope.current === requestedScope
            ) {
                setError(customFieldApiError(requestError))
            }
        } finally {
            if (
                request === latestHistoryRequest.current &&
                activeValueScope.current === requestedScope
            ) {
                setHistoryLoading(false)
            }
        }
    }

    const rollback = async (entry: CustomFieldHistoryEntry) => {
        const requestedScope = valueScope
        const request = ++latestMutationRequest.current
        setSaving(true)
        setError(null)
        try {
            await customFieldsApi.rollback(
                profileId,
                entityType,
                entry.id,
                current?.version ?? null,
            )
            if (
                request === latestMutationRequest.current &&
                activeValueScope.current === requestedScope
            ) {
                await loadHistory(false)
                if (
                    request === latestMutationRequest.current &&
                    activeValueScope.current === requestedScope
                ) {
                    refresh()
                }
            }
        } catch (requestError) {
            if (
                request === latestMutationRequest.current &&
                activeValueScope.current === requestedScope
            ) {
                setError(customFieldApiError(requestError))
            }
        } finally {
            if (
                request === latestMutationRequest.current &&
                activeValueScope.current === requestedScope
            ) {
                setSaving(false)
            }
        }
    }

    return (
        <div className="rounded border border-gray-200 p-4">
            <div className="mb-2 font-medium">{definition.name}</div>
            <div className="flex flex-wrap items-center gap-2">
                {definition.data_type === CUSTOM_FIELD_DATA_TYPES.Boolean ? (
                    <select
                        aria-label={`${definition.name} value`}
                        className={fieldClass}
                        value={value === null ? '' : value ? 'true' : 'false'}
                        onChange={(event) =>
                            setValue(
                                event.target.value === '' ? null : event.target.value === 'true',
                            )
                        }
                    >
                        <option value="">Not set</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                ) : (
                    <input
                        aria-label={`${definition.name} value`}
                        className={fieldClass}
                        value={typeof value === 'string' ? value : ''}
                        onChange={(event) => setValue(event.target.value)}
                        maxLength={
                            definition.data_type === CUSTOM_FIELD_DATA_TYPES.String
                                ? 250
                                : undefined
                        }
                        placeholder={
                            definition.data_type === CUSTOM_FIELD_DATA_TYPES.Number
                                ? '0.00'
                                : 'Value'
                        }
                    />
                )}
                <Button
                    onClick={() => mutate(false)}
                    disabled={saving || value === null || (valueQuotaReached && !current)}
                >
                    Set
                </Button>
                <Button
                    variant="outline"
                    onClick={() => mutate(true)}
                    disabled={saving || !current}
                >
                    Clear
                </Button>
                <Button variant="ghost" onClick={() => loadHistory(false)} disabled={saving}>
                    History
                </Button>
                <span className="text-xs text-gray-500">
                    {current ? `Version ${current.version}` : 'Not set'}
                </span>
            </div>
            {error && <Callout className="mt-3" intent="danger" title={error} />}
            {history && (
                <div className="mt-3 space-y-2 border-t pt-3 text-sm">
                    {history.map((entry) => (
                        <div
                            key={entry.id}
                            className="flex flex-wrap items-center justify-between gap-2"
                        >
                            <span>
                                <span>
                                    {entry.operation === 'clear'
                                        ? 'Cleared'
                                        : `Set to ${String(entry.value)}`}{' '}
                                    · {new Date(entry.created_at).toLocaleString()}
                                </span>
                                <span className="block text-xs text-gray-500">
                                    {customFieldHistoryContext(entry)}
                                </span>
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => rollback(entry)}
                                disabled={saving}
                            >
                                Undo this change
                            </Button>
                        </div>
                    ))}
                    {history.length === 0 && <span>No history.</span>}
                    {historyCursor && (
                        <Button
                            size="sm"
                            variant="ghost"
                            disabled={historyLoading}
                            onClick={() => loadHistory(true)}
                        >
                            {historyLoading ? 'Loading…' : 'Load older history'}
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}
